import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { AgChartsModule } from 'ag-charts-angular';
import { AgChartOptions } from 'ag-charts-community';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { take } from 'rxjs';
import { AccountingReportsService } from '../accounting-reports.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { SalesTaxPdfGenerator } from './sales-tax-pdf';

@Component({
  selector: 'app-sales-tax-libility',
  standalone: true,
  imports: [FormsModule, CurrencyPipe, DecimalPipe, AgChartsModule, CollapseModule],
  templateUrl: './sales-tax-libility.component.html',
  styleUrl: './sales-tax-libility.component.css'
})
export class SalesTaxLibilityComponent implements OnInit {
  public chartOptions!: AgChartOptions;
  public taxabilityOptions!: AgChartOptions;
  public period: string = 'This Year';
  public periods: string[] = ['This Month', 'Last Month', 'This Quarter', 'This Year', 'Custom'];
  public isChartCollapsed: boolean = false;
  public isBreakdownCollapsed: boolean = false;
  public startDate: string = '';
  public endDate: string = '';

  // Summary Metrics
  public totalGrossSales: number = 0;
  public totalNonTaxable: number = 0;
  public totalTaxable: number = 0;
  public totalTaxCollected: number = 0;

  // Mock Data for Breakdown
  public taxJurisdictions: any[] = [];
  public chartData: any[] = [];

  constructor(
    private reportsService: AccountingReportsService,
    private sharedService: SharedService
  ) {}

  exportPdf() {
    const generator = new SalesTaxPdfGenerator();
    const companyInfo = this.sharedService.company_info;
    const data = {
      totalGrossSales: this.totalGrossSales,
      totalNonTaxable: this.totalNonTaxable,
      totalTaxable: this.totalTaxable,
      totalTaxCollected: this.totalTaxCollected,
      jurisdictions: this.taxJurisdictions
    };
    const doc = generator.generatePdf(data, companyInfo, this.period, this.startDate, this.endDate);
    doc.save(`Sales_Tax_Liability_${this.period.replace(' ', '_')}.pdf`);
  }

  ngOnInit() {
    if (this.sharedService.company_info_loaded) {
      this.setPeriod('This Year');
      return;
    }

    this.sharedService.company_info_retrieved$.pipe(take(1)).subscribe(() => {
      this.setPeriod('This Year');
    });
  }

  loadData() {
    this.reportsService.getSalesTaxLiability(this.startDate, this.endDate).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const d = res.data;
          this.totalGrossSales = d.totalGrossSales || 0;
          this.totalNonTaxable = d.totalNonTaxable || 0;
          this.totalTaxable = d.totalTaxable || 0;
          this.totalTaxCollected = d.totalTaxCollected || 0;
          this.taxJurisdictions = d.jurisdictions || [];
          this.chartData = d.chartData || [];
          this.initChart();
        }
      },
      error: (err) => console.error("Error loading Sales Tax data:", err)
    });
  }

  setPeriod(p: string) {
    this.period = p;
    if (p === 'Custom') return;

    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (p === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (p === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (p === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    } else if (p === 'This Year') {
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
    }

    this.startDate = this.formatDate(start);
    this.endDate = this.formatDate(end);
    this.loadData();
  }

  applyCustomRange() {
    if (this.startDate && this.endDate) {
      this.loadData();
    }
  }

  private formatDate(date: Date): string {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  private initChart() {
    // 1. Tax Trend (Existing)
    this.chartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Tax Collection Trend', color: 'white', fontSize: 13 },
      data: this.chartData,
      series: [
        {
          type: 'bar',
          xKey: 'month',
          yKey: 'taxCollected',
          yName: 'Tax Collected',
          fill: '#ff9800',
          cornerRadius: 4,
          label: {
            enabled: true,
            color: 'white',
            fontWeight: 500,
            formatter: (params) => '$' + (params.value / 1000).toFixed(1) + 'k'
          }
        }
      ],
      axes: [
        {
          type: 'category',
          position: 'bottom',
          label: { color: 'rgba(255, 255, 255, 0.7)' }
        },
        {
          type: 'number',
          position: 'left',
          label: { 
            color: 'rgba(255, 255, 255, 0.7)',
            formatter: (params: any) => '$' + (params.value / 1000).toFixed(0) + 'k'
          },
          gridLine: { style: [{ stroke: 'rgba(255, 255, 255, 0.05)' }] }
        }
      ],
      legend: { enabled: false }
    } as any;

    // 2. Taxability Breakdown (Donut)
    this.taxabilityOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Taxability Composition', color: 'white', fontSize: 13 },
      data: [
        { type: 'Taxable', amount: this.totalTaxable },
        { type: 'Non-Taxable', amount: this.totalNonTaxable }
      ],
      series: [
        {
          type: 'donut',
          angleKey: 'amount',
          calloutLabelKey: 'type',
          innerRadiusRatio: 0.7,
          strokeWidth: 0,
          sectorLabelKey: 'amount',
          sectorLabel: {
            color: 'white',
            fontWeight: 500,
            formatter: (params) => '$' + (params.value / 1000).toFixed(0) + 'k',
          },
          fills: ['#9c27b0', '#9e9e9e'],
        },
      ],
      legend: { enabled: true, position: 'bottom' },
    } as any;
  }
}
